/**
 * Provider-neutral deployment status shared with the backend `deploymentService`.
 * The editor toolbar reflects exactly what the backend reports — it never invents
 * a "live" state or runs a fake timer that pretends a deployment finished.
 */
export type DeploymentStatus = 'commit-created' | 'queued' | 'building' | 'ready' | 'failed';

export interface Deployment {
  id: string;
  status: DeploymentStatus | string;
  provider: string;
  commitSha: string | null;
  detail: string | null;
  createdAt: string;
  updatedAt: string;
}

export type DeploymentTone = 'neutral' | 'progress' | 'success' | 'error';

export interface DeploymentDisplay {
  label: string;
  tone: DeploymentTone;
  detail: string | null;
  /** Whether the status may still change and is worth polling for. */
  isPending: boolean;
}

const PENDING_STATUSES = new Set<string>(['commit-created', 'queued', 'building']);

export function isPendingDeployment(deployment: Deployment | null): boolean {
  return !!deployment && PENDING_STATUSES.has(deployment.status);
}

/**
 * Map a deployment (or its absence) to toolbar display props. `null` means the
 * site genuinely has no deployment history — we say so rather than guess.
 */
export function describeDeploymentStatus(deployment: Deployment | null): DeploymentDisplay {
  if (!deployment) {
    return {
      label: 'Status unavailable',
      tone: 'neutral',
      detail: 'Live deployment status is not available for this site yet.',
      isPending: false,
    };
  }

  switch (deployment.status) {
    case 'ready':
      return {
        label: 'Live',
        tone: 'success',
        detail: deployment.detail ?? 'Latest changes are live.',
        isPending: false,
      };
    case 'failed':
      return {
        label: 'Deploy failed',
        tone: 'error',
        detail: deployment.detail ?? 'The last deployment failed.',
        isPending: false,
      };
    case 'building':
      return {
        label: 'Building',
        tone: 'progress',
        detail: deployment.detail ?? 'Building the site.',
        isPending: true,
      };
    case 'queued':
      return {
        label: 'Queued',
        tone: 'progress',
        detail: deployment.detail ?? 'Queued for build.',
        isPending: true,
      };
    case 'commit-created':
      return {
        label: 'Committed',
        tone: 'progress',
        detail: deployment.detail ?? 'Committed. Awaiting build confirmation.',
        isPending: true,
      };
    default:
      return {
        label: 'Status unavailable',
        tone: 'neutral',
        detail: null,
        isPending: false,
      };
  }
}
